"use client";

import { Button } from "@/components/ui/Button";
import { CheckboxIcon, CheckboxIndicator, CheckboxRoot } from "@/components/ui/Checkbox";
import { rpc } from "@/data/rpc.client";
import type { BlokShoeFinder, BlokShoeFinderStep } from "@/storyblok";
import { clsx } from "@frend-digital/ui";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";

import Image from "next/image";
import styles from "./ShoeFinderStep.module.css";
import { buildFiltersFromSelections } from "./filters";
import { shoeFinderSelectionsAtom, shoeFinderStepAtom } from "./state";
import { trackShoeFinderStepComplete } from "./tracking";

interface ShoeFinderStepProps {
	blok: BlokShoeFinder;
	visibleSteps: BlokShoeFinderStep[];
}

export const ShoeFinderStep = ({ blok, visibleSteps }: ShoeFinderStepProps) => {
	const [stepIndex, setStepIndex] = useAtom(shoeFinderStepAtom);
	const [selections, setSelections] = useAtom(shoeFinderSelectionsAtom);

	const step = visibleSteps[stepIndex];
	const stepUid = step?._uid;
	const isLastStep = step?.isLastStep ?? false;

	const filters = useMemo(
		() => buildFiltersFromSelections(visibleSteps, selections, stepIndex),
		[visibleSteps, selections, stepIndex],
	);

	const previewFilters = useMemo(
		() => buildFiltersFromSelections(visibleSteps, selections, stepIndex + 1),
		[visibleSteps, selections, stepIndex],
	);

	const sizesQuery = useQuery({
		queryKey: ["shoeFinderSizes", filters],
		queryFn: () => rpc.shoeFinder.getFilters(filters),
		enabled: isLastStep,
	});

	const availableSizes = useMemo(() => {
		if (!sizesQuery.data) return [];
		const sizeFilter = sizesQuery.data.filters.find((f) => f.field === "items.name");
		// "1" and "ONESZ" are placeholder sizes on non-shoe items (e.g. gift
		// cards, insoles) that shouldn't surface as a pickable size here.
		const values = (sizeFilter?.values ?? []).filter((v) => v.value !== "1" && v.value !== "ONESZ");
		return [...values].sort((a, b) => {
			const numA = Number.parseFloat(a.value);
			const numB = Number.parseFloat(b.value);
			if (Number.isNaN(numA) && Number.isNaN(numB)) return a.value.localeCompare(b.value);
			if (Number.isNaN(numA)) return 1;
			if (Number.isNaN(numB)) return -1;
			return numA - numB;
		});
	}, [sizesQuery.data]);

	const goForward = useCallback(() => {
		if (step && stepUid) {
			const selected = Array.from(selections.get(stepUid) ?? []);
			const isSkipped = selected.length === 0 && (step.skippable ?? false);

			let selectedLabels: string[];
			if (isLastStep) {
				const labelMap = new Map(availableSizes.map((s) => [s.value, s.name]));
				selectedLabels = selected.map((value) => labelMap.get(value) ?? value);
			} else {
				const labelMap = new Map(step.answers?.map((a) => [a._uid, a.label]) ?? []);
				selectedLabels = selected.map((uid) => labelMap.get(uid) ?? uid);
			}

			trackShoeFinderStepComplete({
				stepIndex,
				stepUid,
				stepTitle: step.title,
				selectedUids: selected,
				selectedLabels,
				isSkipped,
				isLastStep,
			});
		}
		setStepIndex((prev) => prev + 1);
	}, [step, stepUid, stepIndex, selections, isLastStep, availableSizes, setStepIndex]);

	const maxAnswers = step?.maxAnswers ? Number(step.maxAnswers) : undefined;

	const toggleAnswer = useCallback(
		(uid: string) => {
			if (!stepUid) return;
			setSelections((prev) => {
				const next = new Map(prev);
				const current = new Set(next.get(stepUid) ?? []);

				if (step?.multiSelect) {
					if (current.has(uid)) {
						current.delete(uid);
					} else {
						if (maxAnswers && current.size >= maxAnswers) return prev;
						current.add(uid);
					}
				} else {
					if (current.has(uid)) {
						current.clear();
					} else {
						current.clear();
						current.add(uid);
					}
				}

				next.set(stepUid, current);
				return next;
			});
		},
		[stepUid, step?.multiSelect, maxAnswers, setSelections],
	);

	const toggleSize = useCallback(
		(size: string) => {
			if (!stepUid) return;
			setSelections((prev) => {
				const next = new Map(prev);
				const current = new Set(next.get(stepUid) ?? []);

				if (current.has(size)) {
					current.delete(size);
				} else {
					if (current.size >= 2) return prev;
					current.add(size);
				}

				next.set(stepUid, current);
				return next;
			});
		},
		[stepUid, setSelections],
	);

	const selected = (stepUid && selections.get(stepUid)) || new Set<string>();
	const inStockSizes = useMemo(
		() => availableSizes.filter((size) => size.filterCount > 0),
		[availableSizes],
	);
	const inStockSizeValues = useMemo(
		() => new Set(inStockSizes.map((size) => size.value)),
		[inStockSizes],
	);
	// The size step can legitimately have zero in-stock sizes (e.g. every
	// remaining candidate happens to lack the one size left) — don't let that
	// strand the user with no way to reach their result.
	const noSizesToPick = isLastStep && sizesQuery.isSuccess && inStockSizes.length === 0;
	const hasSelection = selected.size > 0 || noSizesToPick;

	const productCountQuery = useQuery({
		queryKey: ["shoeFinderPreviewCount", previewFilters],
		queryFn: () => rpc.shoeFinder.getFilters(previewFilters),
		enabled: hasSelection,
	});

	const availableProductCount = productCountQuery.data?.total ?? null;

	if (!step) return null;

	return (
		<div className={styles.root}>
			<div className={styles.heading}>
				<h2 className={styles.title}>{step.title}</h2>
				{step.description && <p className={styles.description}>{step.description}</p>}
			</div>

			{isLastStep ? (
				<div className={styles.sizeGrid}>
					{availableSizes.map((size) => {
						const isSelected = selected.has(size.value);
						const isSoldOut = !inStockSizeValues.has(size.value);
						return (
							<button
								key={size.value}
								type="button"
								data-stock={isSoldOut ? "no" : "yes"}
								disabled={isSoldOut}
								aria-label={`${size.name}, ${isSoldOut ? "sold out" : "in stock"}`}
								className={clsx(styles.sizePill, isSelected && styles.sizePillSelected)}
								onClick={() => !isSoldOut && toggleSize(size.value)}
							>
								{size.name}
							</button>
						);
					})}
				</div>
			) : (
				<div className={styles.selectionGrid}>
					{step.answers?.map((answer) => {
						const isSelected = selected.has(answer._uid);
						return (
							<button
								key={answer._uid}
								type="button"
								className={clsx(styles.selector, isSelected && styles.selectorSelected)}
								onClick={() => toggleAnswer(answer._uid)}
							>
								<div className={styles.selectorTop}>
									{answer.icon?.filename && (
										<Image
											src={answer.icon.filename}
											alt={answer.icon.alt || answer.label}
											width={55}
											height={55}
											className={styles.selectorIcon}
										/>
									)}
									<CheckboxRoot
										checked={isSelected}
										className={styles.checkbox}
										data-variant="white"
										tabIndex={-1}
										render={<span />}
									>
										<CheckboxIndicator>
											<CheckboxIcon />
										</CheckboxIndicator>
									</CheckboxRoot>
								</div>
								<div className={styles.selectorText}>
									<span className={styles.selectorLabel}>{answer.label}</span>
									{answer.description && (
										<span className={styles.selectorDescription}>{answer.description}</span>
									)}
								</div>
							</button>
						);
					})}
				</div>
			)}

			<div className={styles.actions}>
				{hasSelection ? (
					<>
						{availableProductCount !== null && (
							<span className={styles.ctaText}>
								{(blok.ctaProductsAvailable || "{{count}} products available").replace(
									/\{\{count\}\}/g,
									String(availableProductCount),
								)}
							</span>
						)}
						{availableProductCount === 0 && (
							<span className={styles.noMatchesText}>
								{(blok.ctaNoMatchesLabel as string | undefined) ||
									"No matches found for this combination. Adjust your choices to see results."}
							</span>
						)}
						<Button
							color="black"
							className={styles.continueButton}
							loading={productCountQuery.isLoading}
							disabled={availableProductCount === 0}
							onClick={goForward}
						>
							{isLastStep
								? blok.ctaLastStepLabel || "Show me options"
								: blok.ctaContinueLabel || "Continue"}
						</Button>
					</>
				) : isLastStep && sizesQuery.isLoading ? null : step?.skippable ? (
					<Button color="black" className={styles.continueButton} onClick={goForward}>
						{blok.ctaContinueLabel || "Continue"}
					</Button>
				) : (
					<span className={styles.ctaText}>{blok.ctaEmptyLabel || "Make a selection"}</span>
				)}
			</div>
		</div>
	);
};
